import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enUS, plPL } from "@mui/x-date-pickers/locales";
import type { PickersInputLocaleText } from "@mui/x-date-pickers/locales";
import "dayjs/locale/pl";
import "dayjs/locale/en";

const getLocaleText = (
  locale: typeof plPL | typeof enUS
): PickersInputLocaleText =>
  locale.components.MuiLocalizationProvider.defaultProps
    .localeText as PickersInputLocaleText;

export function useDatePickerLocalization() {
  const { i18n } = useTranslation();

  return useMemo(() => {
    if (i18n.language === "pl") {
      return {
        adapterLocale: "pl" as const,
        localeText: getLocaleText(plPL),
      };
    }

    return {
      adapterLocale: "en" as const,
      localeText: getLocaleText(enUS),
    };
  }, [i18n.language]);
}

export function DatePickerLocalizationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { adapterLocale, localeText } = useDatePickerLocalization();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={adapterLocale}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
}
